import { Search, RotateCcw, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DateRangePicker } from '@/components/DateRangePicker'
import { DepartmentScopeFilter } from '@/components/department-scope-filter/DepartmentScopeFilter'
import { EmployeeTreeFilter } from '@/components/employee-tree-filter'
import { ORDER_STATUS_FLOW, ORDER_STATUS_LABELS } from '@/schemas/supplier'
import type { EmployeeManualOrderListParams } from '@/api/employee-manual-order'

interface ManualOrderFilterBarProps {
  params: EmployeeManualOrderListParams
  onParamsChange: (params: Partial<EmployeeManualOrderListParams>) => void
  onSearch: () => void
  onReset: () => void
  onStatusChange?: (status: string | undefined) => void
  onExport?: () => void
  isExporting?: boolean
}

// 状态选项按业务流程排序（ORDER_STATUS_FLOW）
const statusOptions = [
  { value: '', label: '全部状态' },
  ...ORDER_STATUS_FLOW.map((status) => ({ value: String(status), label: ORDER_STATUS_LABELS[status] })),
]

export function ManualOrderFilterBar({
  params,
  onParamsChange,
  onSearch,
  onReset,
  onStatusChange,
  onExport,
  isExporting,
}: ManualOrderFilterBarProps) {
  const handleDateChange = (value: { start_date?: string; end_date?: string }) => {
    if (value.start_date && value.end_date) {
      onParamsChange({ date: `${value.start_date} - ${value.end_date}` })
    } else {
      onParamsChange({ date: undefined })
    }
  }

  const dateValue = params.date
    ? { start_date: params.date.split(' - ')[0], end_date: params.date.split(' - ')[1] }
    : undefined

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      onSearch()
    }
  }

  const handleStatusClick = (status: string) => {
    const nextStatus = status || undefined
    if (onStatusChange) {
      onStatusChange(nextStatus)
      return
    }

    onParamsChange({ status: nextStatus })
  }

  return (
    <div className="space-y-4 rounded-lg border bg-card p-4">
      <div className="flex flex-wrap gap-2">
        {statusOptions.map((option) => (
          <Badge
            key={option.value}
            variant={params.status === option.value ? 'default' : 'outline'}
            className="cursor-pointer hover:bg-primary/80"
            onClick={() => handleStatusClick(option.value)}
          >
            {option.label}
          </Badge>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="text-sm font-medium mb-1.5 block">录入人员</label>
          <DepartmentScopeFilter
            value={{ scope: params.scope || 'my', department_id: params.department_id }}
            onChange={(scopeValue) =>
            onParamsChange({
              department_id: undefined,
              ...scopeValue,
              // 本页未启用「无归属」
              scope: scopeValue.scope === 'unassigned' ? undefined : scopeValue.scope,
            })
          }
          />
        </div>

        <div className="w-[220px]">
          <label htmlFor="manual-order-filter-no" className="text-sm font-medium mb-1.5 block">订单号</label>
          <Input
            id="manual-order-filter-no"
            placeholder="输入订单号"
            value={params.order_no || ''}
            onChange={(event) => onParamsChange({ order_no: event.target.value || undefined })}
            onKeyDown={handleKeyDown}
          />
        </div>

        <div className="w-[220px]">
          <label htmlFor="manual-order-filter-delivery" className="text-sm font-medium mb-1.5 block">快递单号</label>
          <Input
            id="manual-order-filter-delivery"
            placeholder="输入快递单号"
            value={params.delivery_id || ''}
            onChange={(event) => onParamsChange({ delivery_id: event.target.value || undefined })}
            onKeyDown={handleKeyDown}
          />
        </div>

        <div className="w-[220px]">
          <label htmlFor="manual-order-filter-realname" className="text-sm font-medium mb-1.5 block">收货人</label>
          <Input
            id="manual-order-filter-realname"
            placeholder="输入姓名/手机号"
            value={params.real_name || ''}
            onChange={(event) => onParamsChange({ real_name: event.target.value || undefined })}
            onKeyDown={handleKeyDown}
          />
        </div>

        <div className="w-[260px]">
          <EmployeeTreeFilter
            label="创建人"
            placeholder="选择创建人"
            hideDepartmentScope
            value={params.creator_ids}
            onChange={(ids) => onParamsChange({ creator_ids: ids.length > 0 ? ids : undefined })}
          />
        </div>

        <div>
          <label className="text-sm font-medium mb-1.5 block">下单时间</label>
          <DateRangePicker value={dateValue} onChange={handleDateChange} />
        </div>

        <div className="w-[140px]">
          <label htmlFor="manual-order-filter-store" className="text-sm font-medium mb-1.5 block">仓库类型</label>
          <Select
            value={params.is_store !== undefined ? String(params.is_store) : 'all'}
            onValueChange={(value) => onParamsChange({ is_store: value === 'all' ? undefined : Number(value) })}
          >
            <SelectTrigger id="manual-order-filter-store">
              <SelectValue placeholder="仓库类型" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部</SelectItem>
              <SelectItem value="1">仓库保管</SelectItem>
              <SelectItem value="0">非仓库保管</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="w-[140px]">
          <label htmlFor="manual-order-filter-cash" className="text-sm font-medium mb-1.5 block">付款类型</label>
          <Select
            value={params.is_cash !== undefined ? String(params.is_cash) : 'all'}
            onValueChange={(value) => onParamsChange({ is_cash: value === 'all' ? undefined : Number(value) })}
          >
            <SelectTrigger id="manual-order-filter-cash">
              <SelectValue placeholder="付款类型" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部</SelectItem>
              <SelectItem value="1">货到付款</SelectItem>
              <SelectItem value="0">非货到付款</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="w-[140px]">
          <label htmlFor="manual-order-filter-shipping" className="text-sm font-medium mb-1.5 block">发货类型</label>
          <Select
            value={params.shipping_partial !== undefined ? String(params.shipping_partial) : 'all'}
            onValueChange={(value) => onParamsChange({ shipping_partial: value === 'all' ? undefined : Number(value) })}
          >
            <SelectTrigger id="manual-order-filter-shipping">
              <SelectValue placeholder="发货类型" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部</SelectItem>
              <SelectItem value="1">部分发货</SelectItem>
              <SelectItem value="0">全部发货</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="w-[120px]">
          <label htmlFor="manual-order-filter-new" className="text-sm font-medium mb-1.5 block">订单类型</label>
          <Select
            value={params.is_new !== undefined ? String(params.is_new) : 'all'}
            onValueChange={(value) => onParamsChange({ is_new: value === 'all' ? undefined : Number(value) })}
          >
            <SelectTrigger id="manual-order-filter-new">
              <SelectValue placeholder="订单类型" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部</SelectItem>
              <SelectItem value="1">新开</SelectItem>
              <SelectItem value="0">复购</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex gap-2">
          <Button onClick={onSearch}>
            <Search className="mr-1.5 h-4 w-4" />
            查询
          </Button>
          <Button variant="outline" onClick={onReset}>
            <RotateCcw className="mr-1.5 h-4 w-4" />
            重置
          </Button>
          {onExport && (
            <Button variant="outline" onClick={onExport} disabled={isExporting}>
              <Download className="mr-1.5 h-4 w-4" />
              按筛选导出
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
